import type { SnapshotId } from '@ipfabric/utils';
import type { AimIntegrationDraft } from '../../../model/aimDrafts';

/**
 * Fields the form can change, as sent to the verify endpoints.
 * `apiSecret` is separate because only some of the requests carry it.
 */
type AimIntegrationVerifyFields = {
  platform: 'Illumio';
  apiUrl: string;
  apiKey: string;
  organizationId: string;
  slug: string;
  comment: string | null;
};

export type AimIntegrationVerifyRequest =
  /** POST /aim/integrations/verify — everything needed is in the body. */
  | {
      kind: 'body';
      body: AimIntegrationVerifyFields & { apiSecret: string };
    }
  /** POST /aim/integrations/{id}/verify */
  | { kind: 'stored'; id: string; body: AimIntegrationVerifyFields }
  /** POST /snapshots/{snapshotId}/aim/integrations/{id}/verify */
  | {
      kind: 'storedInSnapshot';
      snapshotId: SnapshotId;
      id: string;
      body: AimIntegrationVerifyFields;
    };

export type ResolveAimIntegrationVerifyRequestArgs = {
  /** Current form values. `apiSecret` is blank unless the user typed one. */
  values: AimIntegrationVerifyFields & { apiSecret: string };
  /** The row being edited, or undefined in add mode. */
  initialValues: AimIntegrationDraft | undefined;
  /** Snapshot the settings form belongs to, or null for global settings. */
  snapshotId: SnapshotId | null;
};

/**
 * Pick which verify endpoint to call, and with what body.
 *
 * The deciding question is whether we hold the secret in plaintext:
 *
 * - the user typed one → send it (`body`)
 * - the row was added in this unsaved form session → its draft secret is still
 *   plaintext, so send that (`body`)
 * - otherwise the row is saved and the draft only holds the encrypted secret,
 *   which the backend refuses → let the backend read and decrypt its own copy
 *   (`stored` / `storedInSnapshot`, by settings scope)
 */
export const resolveAimIntegrationVerifyRequest = ({
  values,
  initialValues,
  snapshotId,
}: ResolveAimIntegrationVerifyRequestArgs): AimIntegrationVerifyRequest => {
  const fields: AimIntegrationVerifyFields = {
    platform: values.platform,
    apiUrl: values.apiUrl,
    apiKey: values.apiKey,
    organizationId: values.organizationId,
    slug: values.slug,
    comment: values.comment,
  };

  const plaintextSecret =
    values.apiSecret ||
    (initialValues?.unsaved ? initialValues.apiSecret : undefined);

  if (plaintextSecret) {
    return { kind: 'body', body: { ...fields, apiSecret: plaintextSecret } };
  }

  // No plaintext secret means we are editing a saved row, so it has an id.
  const id = initialValues?.id;
  if (!id) {
    return { kind: 'body', body: { ...fields, apiSecret: values.apiSecret } };
  }

  return snapshotId === null
    ? { kind: 'stored', id, body: fields }
    : { kind: 'storedInSnapshot', snapshotId, id, body: fields };
};
